import { ThirdPartyRefundProvider } from "@redotech/cockroachdb-codegen/congodb/third-party-refund-provider";

/**
 * Wallet providers paid by customer-supplied contact info, as opposed to
 * REFUNDID which needs no recipient details from the customer.
 */
export const REFUND_DESTINATION_PROVIDERS = [
  ThirdPartyRefundProvider.Paypal,
  ThirdPartyRefundProvider.Venmo,
] as const;

export type RefundDestinationProvider =
  (typeof REFUND_DESTINATION_PROVIDERS)[number];

export function isRefundDestinationProvider(
  value: unknown,
): value is RefundDestinationProvider {
  return REFUND_DESTINATION_PROVIDERS.includes(
    value as RefundDestinationProvider,
  );
}

/** Rails where Redo pays the customer directly, so a disbursement is recorded at submission. */
export const REDO_FUNDED_REFUND_PROVIDERS = [
  ...REFUND_DESTINATION_PROVIDERS,
  ThirdPartyRefundProvider.Stripe,
] as const;

export type RedoFundedRefundProvider =
  (typeof REDO_FUNDED_REFUND_PROVIDERS)[number];

export function isRedoFundedRefundProvider(
  value: unknown,
): value is RedoFundedRefundProvider {
  return REDO_FUNDED_REFUND_PROVIDERS.includes(
    value as RedoFundedRefundProvider,
  );
}

// value set {email,phone} coincides with the unrelated account_identifier_type DB enum //redontIgnore
export enum RefundRecipientField {
  EMAIL = "email",
  PHONE = "phone",
}

export enum RefundRecipientError {
  MISSING = "missing",
  INVALID_EMAIL = "invalid_email",
  INVALID_US_PHONE = "invalid_us_phone",
}

/** PayPal pays a wallet by email; Venmo payouts require a US mobile number. */
export function recipientFieldForProvider(
  provider: RefundDestinationProvider,
): RefundRecipientField {
  return provider === ThirdPartyRefundProvider.Venmo
    ? RefundRecipientField.PHONE
    : RefundRecipientField.EMAIL;
}

export type RefundRecipientValidation =
  | { valid: true; normalized: string }
  | { valid: false; error: RefundRecipientError };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export function normalizeRecipientEmail(
  raw: string | null | undefined,
): RefundRecipientValidation {
  const trimmed = (raw ?? "").trim().toLowerCase();
  if (!trimmed) {
    return { valid: false, error: RefundRecipientError.MISSING };
  }
  if (!EMAIL_PATTERN.test(trimmed)) {
    return { valid: false, error: RefundRecipientError.INVALID_EMAIL };
  }
  return { valid: true, normalized: trimmed };
}

/**
 * Venmo payouts are US-only, so this accepts NANP numbers and emits E.164.
 * Mobile-vs-landline is not detectable here; PayPal rejects landlines at payout.
 */
export function normalizeUsPhone(
  raw: string | null | undefined,
): RefundRecipientValidation {
  if (!(raw ?? "").trim()) {
    return { valid: false, error: RefundRecipientError.MISSING };
  }
  const digits = (raw ?? "").replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length !== 10) {
    return { valid: false, error: RefundRecipientError.INVALID_US_PHONE };
  }
  const areaCode = national[0];
  const exchange = national[3];
  if (
    areaCode === "0" ||
    areaCode === "1" ||
    exchange === "0" ||
    exchange === "1"
  ) {
    return { valid: false, error: RefundRecipientError.INVALID_US_PHONE };
  }
  return { valid: true, normalized: `+1${national}` };
}

export interface RefundRecipientInput {
  email?: string | null;
  phone?: string | null;
}

export function validateRefundRecipient(
  provider: RefundDestinationProvider,
  input: RefundRecipientInput,
): RefundRecipientValidation {
  return recipientFieldForProvider(provider) === RefundRecipientField.PHONE
    ? normalizeUsPhone(input.phone)
    : normalizeRecipientEmail(input.email);
}

export enum RecipientOrderMatch {
  ORDER_EMAIL = "order_email",
  ORDER_PHONE = "order_phone",
  NONE = "none",
}

export interface OrderContact {
  email?: string | null;
  phone?: string | null;
}

export enum RecipientOrderComparison {
  MATCHED = "matched",
  MISMATCHED = "mismatched",
  /** The order has no usable value of this type, so there is nothing to check. */
  NOT_COMPARABLE = "not_comparable",
}

/**
 * Cross-checks a normalized recipient against the order's contact info.
 * NOT_COMPARABLE is distinct from MISMATCHED: an order with no phone on file
 * must not be reported to the customer as "doesn't match".
 */
export function compareRecipientToOrder(
  field: RefundRecipientField,
  normalized: string,
  orderContact: OrderContact,
): RecipientOrderComparison {
  const orderValue = normalizedOrderContactValue(field, orderContact);
  if (orderValue === undefined) {
    return RecipientOrderComparison.NOT_COMPARABLE;
  }
  return orderValue === normalized
    ? RecipientOrderComparison.MATCHED
    : RecipientOrderComparison.MISMATCHED;
}

/**
 * The order's own value for this field, normalized, or undefined when the
 * order has none or it cannot be parsed. Doubles as the portal's prefill.
 */
export function normalizedOrderContactValue(
  field: RefundRecipientField,
  orderContact: OrderContact,
): string | undefined {
  const result =
    field === RefundRecipientField.EMAIL
      ? normalizeRecipientEmail(orderContact.email)
      : normalizeUsPhone(orderContact.phone);
  return result.valid ? result.normalized : undefined;
}

/** What the recipient was verified against, for persistence. */
export function matchRecipientAgainstOrder(
  field: RefundRecipientField,
  normalized: string,
  orderContact: OrderContact,
): RecipientOrderMatch {
  if (
    compareRecipientToOrder(field, normalized, orderContact) !==
    RecipientOrderComparison.MATCHED
  ) {
    return RecipientOrderMatch.NONE;
  }
  return field === RefundRecipientField.EMAIL
    ? RecipientOrderMatch.ORDER_EMAIL
    : RecipientOrderMatch.ORDER_PHONE;
}

export interface PersistedRefundRecipient {
  email: string | null;
  phone: string | null;
  matchedOrderContact: RecipientOrderMatch;
}

export type ResolvedRefundRecipient =
  | {
      ok: true;
      normalized: string;
      recipient: PersistedRefundRecipient;
      comparison: RecipientOrderComparison;
    }
  | { ok: false; error: RefundRecipientError };

/**
 * Validates and normalizes the field the provider needs, then cross-checks it
 * against the order. The only field the provider uses is stored; the other is
 * cleared so a provider switch cannot leave stale contact info behind.
 */
export function resolveRefundRecipient(
  provider: RefundDestinationProvider,
  input: RefundRecipientInput,
  orderContact: OrderContact,
): ResolvedRefundRecipient {
  const validation = validateRefundRecipient(provider, input);
  if (!validation.valid) {
    return { ok: false, error: validation.error };
  }
  const field = recipientFieldForProvider(provider);
  const matchedOrderContact = matchRecipientAgainstOrder(
    field,
    validation.normalized,
    orderContact,
  );
  return {
    ok: true,
    normalized: validation.normalized,
    comparison: compareRecipientToOrder(
      field,
      validation.normalized,
      orderContact,
    ),
    recipient:
      field === RefundRecipientField.PHONE
        ? { email: null, phone: validation.normalized, matchedOrderContact }
        : { email: validation.normalized, phone: null, matchedOrderContact },
  };
}
